import * as ToastPrimitive from '@radix-ui/react-toast';
import { X } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type ReactNode } from 'react';
import { create } from 'zustand';

import { cn } from '@/lib/utils';

export type ToastVariant = 'default' | 'success' | 'warning' | 'danger';

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Em ms; default 5000. */
  duration?: number;
  /** data-testid customizado no root do toast (substitui `toast-{variant}`). */
  testId?: string;
}

interface ToastItem extends ToastOptions {
  id: string;
}

interface ToastStore {
  items: ToastItem[];
  push: (opts: ToastOptions) => void;
  dismiss: (id: string) => void;
}

// Exportado para que testes possam resetar entre cenários.
export const useToastStore = create<ToastStore>((set) => ({
  items: [],
  push: (opts) =>
    set((state) => ({
      items: [...state.items, { ...opts, id: `${Date.now()}-${Math.random()}` }],
    })),
  dismiss: (id) => set((state) => ({ items: state.items.filter((t) => t.id !== id) })),
}));

export function useToast() {
  const push = useToastStore((s) => s.push);
  return { toast: push };
}

const VARIANT_CLASS: Record<ToastVariant, string> = {
  default: 'border-border bg-surface text-fg',
  success: 'border-success/40 bg-success-50 text-success-700',
  warning: 'border-warning/40 bg-warning-50 text-warning-700',
  danger: 'border-danger/40 bg-danger-50 text-danger-700',
};

const ToastRoot = forwardRef<
  ElementRef<typeof ToastPrimitive.Root>,
  ComponentPropsWithoutRef<typeof ToastPrimitive.Root> & { variant?: ToastVariant }
>(function ToastRoot({ className, variant = 'default', ...props }, ref) {
  return (
    <ToastPrimitive.Root
      ref={ref}
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out pointer-events-auto flex w-full items-start gap-3 border p-3 shadow-paper',
        VARIANT_CLASS[variant],
        className,
      )}
      {...props}
    />
  );
});

function ToastBody({ title, description }: { title: string; description?: ReactNode }) {
  return (
    <div className="flex flex-1 flex-col gap-0.5">
      <ToastPrimitive.Title className="text-body-sm font-medium">{title}</ToastPrimitive.Title>
      {description ? (
        <ToastPrimitive.Description className="text-caption opacity-90">
          {description}
        </ToastPrimitive.Description>
      ) : null}
    </div>
  );
}

export function Toaster() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {items.map((item) => (
        <ToastRoot
          key={item.id}
          variant={item.variant ?? 'default'}
          duration={item.duration ?? 5000}
          onOpenChange={(open) => {
            if (!open) dismiss(item.id);
          }}
          data-testid={item.testId ?? `toast-${item.variant ?? 'default'}`}
        >
          <ToastBody title={item.title} description={item.description} />
          <ToastPrimitive.Close
            aria-label="Fechar"
            className="p-1 text-current opacity-70 transition-opacity duration-fast ease-sc hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </ToastPrimitive.Close>
        </ToastRoot>
      ))}
      <ToastPrimitive.Viewport className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-96 max-w-[100vw] flex-col gap-2 outline-none" />
    </ToastPrimitive.Provider>
  );
}
